/**
 * Панель кнопок редактора исходника: обёртка выделения в маркеры разметки.
 *
 * ПЕРЕОПРЕДЕЛЕНИЕ НОРМЫ. `docs/phase-1/00-integration.md` §Окружение 12 откладывал редактор
 * исходника в фазу 2 (там же `docs/phase-1/05-ui.md` §5: «Фаза 1 — редактора нет»). Решение
 * Ильи 24.08.2026 это отменяет: редактор фазы 1 строится целиком. Где §12 расходится с этим
 * файлом — прав этот файл; остальная часть §12 силы не теряет.
 *
 * Здесь только чистые функции: вход — текст и границы выделения, выход — новый текст и новое
 * выделение. Ни React, ни DOM. Причина не в эстетике: это единственная часть редактора, где
 * легко ошибиться на единицу, и проверять её надо таблицей значений, а не кликами по jsdom.
 *
 * Единицы. `start`/`end` — индексы UTF-16, то есть ровно то, что отдаёт `selectionStart`
 * у textarea. Пересчётов здесь нет и быть не должно: маркеры разметки — ASCII, и вставка
 * их на границе выделения не может разрезать суррогатную пару (docs/ui-design.md §6.5).
 *
 * Чего эти функции НЕ делают — намеренно:
 *   • не проверяют, получится ли из результата валидная разметка. Проверяет `sourceToEntities`
 *     на каждое изменение, и её отказ выключает сохранение. Вторая, приблизительная проверка
 *     здесь означала бы два расходящихся мнения о синтаксисе;
 *   • не подставляют адрес-заглушку в ссылку. `[текст](https://)` разбор проходит и уезжает
 *     в канал ссылкой в никуда, поэтому адрес остаётся пустым: это ошибка разбора с позицией,
 *     и сохранение выключено, пока человек её не исправит.
 */

export interface SourceSelection {
  readonly start: number;
  readonly end: number;
}

export interface SourceEdit {
  readonly text: string;
  readonly selection: SourceSelection;
}

/**
 * Маркеры кнопок. Значения — из таблицы синтаксиса `docs/ui-design.md` §6.5, и это не копия
 * правил: разбор всё равно ведёт `sourceToEntities`, здесь лежит только то, что печатается.
 */
export const MARKERS = {
  bold: '**',
  italic: '_',
  underline: '__',
  strikethrough: '~~',
  spoiler: '||',
  code: '`',
} as const;

export type MarkerKind = keyof typeof MARKERS;

/** Плейсхолдер анкора: ссылка без текста — сущность нулевой длины, её разбор молча выбросит. */
export const LINK_ANCHOR_PLACEHOLDER = 'текст ссылки';

/** Префикс цитаты. `>>` (сворачиваемая) кнопкой не ставится — её пишут руками. */
export const QUOTE_PREFIX = '> ';

const clamp = (n: number, max: number): number => (n < 0 ? 0 : n > max ? max : n);

/** Границы приходят из DOM: они могут прийти перевёрнутыми и за пределами текста. */
function normalize(text: string, start: number, end: number): SourceSelection {
  const a = clamp(Math.min(start, end), text.length);
  const b = clamp(Math.max(start, end), text.length);
  return { start: a, end: b };
}

const isSpace = (c: string): boolean => c === ' ' || c === '\t' || c === '\n' || c === '\r';

/**
 * Пробелы по краям выделения выносятся наружу маркеров. Двойной клик по слову в браузере
 * захватывает хвостовой пробел, и `**слово **` — это сущность, кончающаяся пробелом: Telegram
 * её примет и нарисует жирный пробел, а человек этого не хотел.
 */
function trimRange(text: string, sel: SourceSelection): SourceSelection {
  let { start, end } = sel;
  while (end > start && isSpace(text.charAt(end - 1))) end--;
  while (start < end && isSpace(text.charAt(start))) start++;
  return { start, end };
}

/**
 * Обернуть выделение в парный маркер — или снять его, если выделение уже обнято ровно им.
 *
 * Снятие обязательно, а не украшение: Ctrl+B по уже жирному тексту без него даёт `****жирный****`,
 * то есть две пары маркеров, которые разбор схлопнет в сущность нулевой длины и выбросит. Человек
 * увидел бы, что кнопка «не работает».
 */
export function wrapSelection(
  text: string,
  start: number,
  end: number,
  marker: string,
): SourceEdit {
  const raw = normalize(text, start, end);
  const m = marker.length;

  if (
    raw.end > raw.start &&
    raw.start >= m &&
    text.slice(raw.start - m, raw.start) === marker &&
    text.slice(raw.end, raw.end + m) === marker
  ) {
    return {
      text: text.slice(0, raw.start - m) + text.slice(raw.start, raw.end) + text.slice(raw.end + m),
      selection: { start: raw.start - m, end: raw.end - m },
    };
  }

  const sel = trimRange(text, raw);

  // Пустое (или целиком пробельное) выделение: ставим пару и каретку между маркерами, чтобы
  // следующий набранный символ оказался внутри сущности, а не после неё.
  if (sel.end <= sel.start) {
    const at = raw.start;
    return {
      text: text.slice(0, at) + marker + marker + text.slice(at),
      selection: { start: at + m, end: at + m },
    };
  }

  return {
    text:
      text.slice(0, sel.start) +
      marker +
      text.slice(sel.start, sel.end) +
      marker +
      text.slice(sel.end),
    selection: { start: sel.start + m, end: sel.end + m },
  };
}

/**
 * Ссылка: `[анкор]()`. Адрес пуст намеренно (см. шапку) — каретка встаёт в скобки адреса,
 * и до его заполнения разбор отказывает с внятной позицией.
 *
 * Пустое выделение — единственный случай, когда каретка встаёт не в адрес, а на плейсхолдер
 * анкора: анкора ещё нет, и печатать сначала надо его.
 */
export function wrapLink(text: string, start: number, end: number): SourceEdit {
  const raw = normalize(text, start, end);
  const sel = trimRange(text, raw);
  const empty = sel.end <= sel.start;
  const anchor = empty ? LINK_ANCHOR_PLACEHOLDER : text.slice(sel.start, sel.end);
  const at = empty ? raw.start : sel.start;
  const tail = empty ? raw.start : sel.end;
  const next = `${text.slice(0, at)}[${anchor}]()${text.slice(tail)}`;

  if (empty) return { text: next, selection: { start: at + 1, end: at + 1 + anchor.length } };
  const urlAt = at + anchor.length + 3;
  return { text: next, selection: { start: urlAt, end: urlAt } };
}

/** `>`/`>>` плюс не более одного пробела — ровно тот префикс, который снимает разбор. */
function stripQuote(line: string): string {
  if (!line.startsWith('>')) return line;
  let at = line.startsWith('>>') ? 2 : 1;
  if (line.charAt(at) === ' ') at++;
  return line.slice(at);
}

/**
 * Цитата — префикс целых строк, а не обёртка выделения: `> ` посреди строки ничего не значит
 * (docs/ui-design.md §6.5). Поэтому кнопка работает по всем строкам, которых касается
 * выделение, и снимает префикс, если он уже стоит у всех.
 */
export function toggleQuote(text: string, start: number, end: number): SourceEdit {
  const sel = normalize(text, start, end);
  const first = sel.start === 0 ? 0 : text.lastIndexOf('\n', sel.start - 1) + 1;
  const nl = text.indexOf('\n', sel.end);
  const last = nl === -1 ? text.length : nl;

  const lines = text.slice(first, last).split('\n');
  const quoted = lines.every((l) => l.startsWith('>'));
  const outLines = lines.map((l) => (quoted ? stripQuote(l) : QUOTE_PREFIX + l));

  const starts = offsetsOf(lines, first);
  const outStarts = offsetsOf(outLines, first);
  const map = (p: number): number => {
    let i = 0;
    for (let k = 0; k < starts.length; k++) {
      const s = starts[k];
      if (s !== undefined && s <= p) i = k;
    }
    const within = p - (starts[i] ?? first);
    const grew = (outLines[i] ?? '').length - (lines[i] ?? '').length;
    const shifted = within + grew;
    const max = (outLines[i] ?? '').length;
    return (outStarts[i] ?? first) + clamp(shifted, max);
  };

  return {
    text: text.slice(0, first) + outLines.join('\n') + text.slice(last),
    selection: { start: map(sel.start), end: map(sel.end) },
  };
}

function offsetsOf(lines: readonly string[], base: number): number[] {
  const out: number[] = [];
  let at = base;
  for (const l of lines) {
    out.push(at);
    at += l.length + 1;
  }
  return out;
}
